import { useState } from "react"
import _ from "lodash"
import PropTypes from "prop-types"
import { Card } from "primereact/card"

import CustomButton from "../../../components/Buttons/CustomButton"
import FormErrorMessage from "../FormErrorMessage"
import styles from "../../../styles/Form.module.css"

export default function FormErrorPanel({
  title,
  subHeader,
  transactionId,
  messageText,
  showSupport,
  onGoBack,
  goBackLabel,
  enableCopyButton,
  resubmitCount,
  resubmit,
}) {
  const [copied, setCopied] = useState(false)

  const supportEmailSubject = `Submission error ${transactionId || "unknown"}`
  const supportEmailBody = [
    `Error code: ${transactionId || "N/A"}`,
    `Error message: ${messageText || "N/A"}`,
  ].join("\n")
  const supportEmailHref = `mailto:support@myrecoverylink.com?subject=${encodeURIComponent(
    supportEmailSubject,
  )}&body=${encodeURIComponent(supportEmailBody)}`

  const handleCopyErrorInfo = () => {
    if (!navigator?.clipboard?.writeText) {
      return
    }

    navigator.clipboard
      .writeText(supportEmailBody)
      .then(() => {
        setCopied(true)
        setTimeout(() => setCopied(false), 2000)
      })
      .catch(() => {
        // No-op: avoid unhandled promise rejections when clipboard write is blocked.
      })
  }

  const hasMessageContent = !_.isEmpty(messageText)
  // Hide copy action in environments where Clipboard API write is unavailable.
  const hasClipboardSupport = !!navigator?.clipboard?.writeText
  const shouldShowCopyButton =
    enableCopyButton &&
    hasClipboardSupport &&
    (!_.isEmpty(transactionId) || !_.isEmpty(messageText))

  return (
    <div className={styles.errorContainer}>
      <h2 className={styles.errorContainerHeader}>
        <span className="material-icons">error</span>
        {title}
      </h2>

      {subHeader}

      {showSupport && !_.isEmpty(transactionId) && (
        <h3 className={styles.errorSupportMsg}>
          Please{" "}
          <a className={styles.errorSupportMsgLink} href={supportEmailHref}>
            contact customer support
          </a>{" "}
          and provide the following:
        </h3>
      )}

      <div className={styles.errorCardWrapper}>
        {shouldShowCopyButton && (
          <button
            className={styles.copyButton}
            onClick={handleCopyErrorInfo}
            title="Copy error info"
            type="button"
          >
            <span className="material-icons">
              {copied ? "check" : "content_copy"}
            </span>
          </button>
        )}

        <Card>
          {!_.isEmpty(transactionId) && (
            <div>
              Error code: <code className={styles.errorCode}>{transactionId}</code>
            </div>
          )}

          {hasMessageContent && (
            <div>
              <div className={styles.errorMessageLabel}>Error message:</div>
              <FormErrorMessage msg={messageText} />
            </div>
          )}
        </Card>
      </div>

      {onGoBack && (
        <div className={styles.errorButtonContainer}>
          {/* COMING SOON: Option to resubmit the form after an error occurs. */}
          {/* <CustomButton
            label="Resubmit"
            severity="primary"
            onClick={resubmit}
            disabled={resubmitCount >= 1}
          /> */}
          <CustomButton
            label={goBackLabel}
            severity="primary"
            outlined
            onClick={onGoBack}
          />
        </div>
      )}
    </div>
  )
}

FormErrorPanel.propTypes = {
  title: PropTypes.string.isRequired,
  subHeader: PropTypes.node,
  transactionId: PropTypes.string,
  messageText: PropTypes.string,
  showSupport: PropTypes.bool,
  onGoBack: PropTypes.func,
  goBackLabel: PropTypes.string,
  enableCopyButton: PropTypes.bool,
  resubmitCount: PropTypes.number,
  resubmit: PropTypes.func,
}

FormErrorPanel.defaultProps = {
  subHeader: null,
  transactionId: "",
  messageText: "",
  showSupport: false,
  onGoBack: null,
  goBackLabel: "Go back",
  enableCopyButton: true,
  resubmitCount: 0,
  resubmit: null,
}
